import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import type { Award, PaginatedAwards } from '../../types/types';

// Query response types
export interface GetAwardResponse { getAwardFromId: Award }
export interface GetAwardsResponse { getAwardsFromUserName: Award[] }
export interface GetAwardsByOrganizationResponse { getAwardsByOrganization: Award[] }
export interface GetAwardsForOrganizationResponse { getAwardsForOrganization: PaginatedAwards }
export interface GetSignatureResponse { getSignature: { AwardId: string; Signature: string } }
export interface ResendInvitationResponse { resendInvitation: { AwardId: string } }

const acceptAwardMutation = gql`
    mutation acceptAward($AwardId: String!, $UserName: String!, $Accepted: Boolean!) {
        acceptAward(AwardId: $AwardId, UserName: $UserName, Accepted: $Accepted) {
            AwardId
            UserName
            Accepted
            AcceptDate
            NFTPermalink
        }
    }
`;

const grantAwardMutation = gql`
    mutation grantAward(
        $AwardId: String!,
        $AwardType: String!,
        $RecipientName: String!,
        $GrantDate: String!,
        $AwardDescription: String!,
        $Organization: String!,
        $GrantorEmail: String!,
        $RecipientEmail: String!,
        $NFTLocation: String!,
        $NFTThumbnail: String!,
        $OrganizationId: String!,
        $AwardName: String!,
        $GrantorName: String,
        $Note: String,
        $CC: [String],
        $BCC: [String],
        $Context: String!,
        $ContextURL: String!) {
        grantAward(
            AwardId: $AwardId,
            AwardType: $AwardType,
            RecipientName: $RecipientName,
            GrantDate: $GrantDate,
            AwardDescription: $AwardDescription,
            Organization: $Organization,
            GrantorEmail: $GrantorEmail,
            RecipientEmail: $RecipientEmail,
            NFTLocation: $NFTLocation,
            NFTThumbnail: $NFTThumbnail,
            OrganizationId: $OrganizationId,
            AwardName: $AwardName,
            GrantorName: $GrantorName,
            Note: $Note,
            CC: $CC,
            BCC: $BCC,
            Context: $Context,
            ContextURL: $ContextURL) {
            AwardId,
            AwardType,
            RecipientName,
            GrantDate,
            AwardDescription,
            Organization,
            GrantorEmail,
            GrantorUserName,
            GrantorName,
            RecipientEmail,
            NFTLocation,
            NFTThumbnail,
            OrganizationId,
            Signature,
            Revoked,
            RevocationReason,
            RevocationDate,
            AwardName,
            Note,
            CC,
            BCC,
            Context,
            ContextURL,
        }
    }
`;

const revokeAwardMutation = gql`
    mutation revokeAward($AwardId: String!, $RevocationReason: String!) {
        revokeAward(AwardId: $AwardId, RevocationReason: $RevocationReason) {
            AwardId
        }
    }
`;

const sendAndCollectEmailMutation = gql`
    mutation sendAndCollectEmail(
        $AwardId: String!,
        $NFTLocation: String!,
        $NFTThumbnail: String!,
        $Recipients: [String],
        $Note: String!) {
        sendAndCollectEmail(
            AwardId: $AwardId,
            NFTLocation: $NFTLocation,
            NFTThumbnail: $NFTThumbnail,
            Recipients: $Recipients,
            Note: $Note,
        ) {
            AwardId
        }
    }
`;

const uploadToArweaveMutation = gql`
    mutation uploadToArweave($AwardId: String!) {
        uploadToArweave(AwardId: $AwardId) {
            AwardId
        }
    }
`;

const awardsQuery = gql`
    query getAwards(
        $UserName: String!,
        $AcceptedOnly: Boolean!) {
        getAwardsFromUserName(UserName: $UserName, AcceptedOnly: $AcceptedOnly) {
            UserName
            AwardId
            Accepted
            AwardType
            RecipientName
            RecipientEmail
            GrantDate
            AwardDescription
            Organization
            OrganizationId
            GrantorEmail
            GrantorUserName
            GrantorName
            AcceptDate
            NFTLocation
            NFTThumbnail
            Signature
            Revoked
            RevocationReason
            RevocationDate
            NFTPermalink
            AwardName
            Note
            Context
            ContextURL
        }
    }
`;

const awardWithPKQuery = gql`
    query getAwardFromIdWithPK(
        $AwardId: String!) {
        getAwardFromId(AwardId: $AwardId) {
            UserName
            AwardId
            Accepted
            AwardType
            RecipientName
            RecipientEmail
            GrantDate
            AwardDescription
            Organization
            OrganizationId
            GrantorEmail
            GrantorUserName
            GrantorName
            AcceptDate
            NFTLocation
            NFTThumbnail
            Signature
            Revoked
            RevocationReason
            RevocationDate
            NFTPermalink
            AwardName
            Note
            Context
            ContextURL
            Org {
                OrganizationId
                PublicKey
            }
        }
    }
`;

const awardQuery = gql`
    query getAwardFromId(
        $AwardId: String!) {
        getAwardFromId(AwardId: $AwardId) {
            UserName
            AwardId
            Accepted
            AwardType
            RecipientName
            RecipientEmail
            GrantDate
            AwardDescription
            Organization
            OrganizationId
            GrantorEmail
            GrantorUserName
            GrantorName
            AcceptDate
            NFTLocation
            NFTThumbnail
            Signature
            Revoked
            RevocationReason
            RevocationDate
            NFTPermalink
            AwardName
            Note
            Context
            ContextURL
        }
    }
`;

function useAwardQuery(awardId: string, getPublicKey: boolean, skip = false) {
    return useQuery<GetAwardResponse>(
        getPublicKey ? awardWithPKQuery : awardQuery,
        {
            variables: {
                AwardId: awardId,
            },
            skip,
        },
    );
}

function useAwardsQuery(userName: string, acceptedOnly: boolean) {
    return useQuery<GetAwardsResponse>(
        awardsQuery,
        {
            variables: {
                UserName: userName,
                AcceptedOnly: acceptedOnly,
            },
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
        },
    );
}

const awardsByOrganizationQuery = gql`
    query getAwardsByOrganization($UserName: String!) {
        getAwardsByOrganization(UserName: $UserName) {
            UserName
            AwardId
            Accepted
            AwardType
            RecipientName
            RecipientEmail
            GrantDate
            AwardDescription
            Organization
            OrganizationId
            GrantorEmail
            GrantorUserName
            GrantorName
            AcceptDate
            NFTLocation
            NFTThumbnail
            Signature
            Revoked
            RevocationReason
            RevocationDate
            RevokerUserName
            NFTPermalink
            AwardName
            Note
            Context
            ContextURL
        }
    }
`;

function useAwardsByOrganizationQuery(userName: string) {
    return useQuery<GetAwardsByOrganizationResponse>(
        awardsByOrganizationQuery,
        {
            variables: {
                UserName: userName,
            },
        },
    );
}

const awardsForOrganizationQuery = gql`
    query getAwardsForOrganization(
        $OrganizationId: String!,
        $ExclusiveStartKey: JSON) {
        getAwardsForOrganization(
            OrganizationId: $OrganizationId,
            ExclusiveStartKey: $ExclusiveStartKey) {
            Awards {
                UserName
                AwardId
                Accepted
                AwardType
                RecipientName
                RecipientEmail
                GrantDate
                AwardDescription
                Organization
                OrganizationId
                GrantorEmail
                GrantorUserName
                GrantorName
                AcceptDate
                NFTLocation
                NFTThumbnail
                Signature
                Revoked
                RevocationReason
                RevocationDate
                RevokerUserName
                NFTPermalink
                AwardName
                Note
                Context
                ContextURL
            }
            Paginator {
                LastEvaluatedKey
            }
        }
    }`;

function useAwardsForOrganizationQuery(organizationId: string, exclusiveStartKey: string | null) {
    return useQuery<GetAwardsForOrganizationResponse>(
        awardsForOrganizationQuery,
        {
            variables: {
                OrganizationId: organizationId,
                ExclusiveStartKey: exclusiveStartKey,
            },
        },
    );
}

const getSignatureQuery = gql`
    query getSignature(
        $AwardId: String!,
        $AwardType: String!,
        $RecipientName: String!,
        $GrantDate: String!,
        $AwardDescription: String!,
        $Organization: String!,
        $GrantorEmail: String!,
        $RecipientEmail: String!,
        $NFTLocation: String!,
        $NFTThumbnail: String!,
        $OrganizationId: String!,
        $AwardName: String!,
        $GrantorName: String,
        $Note: String!,
        $CC: [String],
        $BCC: [String],
        $Context: String!,
        $ContextURL: String!) {
        getSignature(
            AwardId: $AwardId,
            AwardType: $AwardType,
            RecipientName: $RecipientName,
            GrantDate: $GrantDate,
            AwardDescription: $AwardDescription,
            Organization: $Organization,
            GrantorEmail: $GrantorEmail,
            RecipientEmail: $RecipientEmail,
            NFTLocation: $NFTLocation,
            NFTThumbnail: $NFTThumbnail,
            OrganizationId: $OrganizationId,
            AwardName: $AwardName,
            GrantorName: $GrantorName,
            Note: $Note,
            CC: $CC,
            BCC: $BCC,
            Context: $Context,
            ContextURL: $ContextURL) {
            AwardId,
            Signature,
        }
    }
`;

const resendInvitationQuery = gql`
    query resendInvitation($AwardId: String!) {
        resendInvitation(AwardId: $AwardId) {
            AwardId
        }
    }
`;

export {
    awardsQuery,
    awardWithPKQuery,
    awardQuery,
    awardsByOrganizationQuery,
    awardsForOrganizationQuery,
    getSignatureQuery,
    resendInvitationQuery,
    acceptAwardMutation,
    grantAwardMutation,
    revokeAwardMutation,
    sendAndCollectEmailMutation,
    uploadToArweaveMutation,
    useAwardQuery,
    useAwardsQuery,
    useAwardsByOrganizationQuery,
    useAwardsForOrganizationQuery,
};
